<template>
  <p v-if="text" class="text-sm text-neutral50" :data-testid="testId">
    {{ text }}
  </p>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import Network from '@/Network'
import { REGISTRATION_GATE_SETTING_NAME } from '../constants'

/**
 * F4 (M5, balão ⑥ / spec §7.2b) — "N plantões deste recorte ficariam bloqueados".
 *
 * Componente porque a previsão tem DOIS pontos de uso: o painel de pós-plantão
 * (onde o gestor está olhando o efeito) e a tela da política da escala (onde ele
 * decide). Duplicar o texto e a tolerância a backend sem rota faria as duas
 * telas divergirem no primeiro ajuste.
 *
 * O texto é NEUTRO quanto ao estado atual do toggle: nem o painel nem a política
 * sabem, em todo recorte, se ele já está ligado — dizer "se X for ligado" mente
 * quando ele já está.
 */
const props = withDefaults(
  defineProps<{
    /** A pergunta só tem resposta para UMA escala: é a restrição do backend. */
    scheduleId: number | null
    from: string | null
    to: string | null
    testId?: string
  }>(),
  { testId: 'registration-block-impact' }
)

/**
 * `null` cobre os dois casos em que não há número — backend sem a rota e recorte
 * incompleto — e nos dois a linha simplesmente não é renderizada. A falha é
 * engolida de propósito: é informação acessória, e um toast de erro aqui
 * interromperia o trabalho por algo que o usuário nem pediu.
 */
const impact = ref<{ total: number; blocked: number } | null>(null)

/** Debounce + sequência: trocar o período duas vezes rápido deixava na tela o número da request antiga. */
const DEBOUNCE_MS = 300
let timer: ReturnType<typeof setTimeout> | null = null
let requestSeq = 0

const text = computed(() => {
  const value = impact.value
  if (!value || value.total === 0) return null
  const plural = value.blocked === 1 ? 'plantão ficaria bloqueado' : 'plantões ficariam bloqueados'
  return (
    `Hoje, ${value.blocked} de ${value.total} ${plural} nesta escala com ` +
    `"${REGISTRATION_GATE_SETTING_NAME}" ligado.`
  )
})

const fetchImpact = async () => {
  const seq = ++requestSeq
  if (props.scheduleId === null || !props.from || !props.to) {
    impact.value = null
    return
  }

  try {
    // Optional chaining: um bundle antigo do service (ou um mock de teste que
    // não conhece o método) não pode derrubar a tela inteira.
    const result =
      (await Network.postStation.getRegistrationBlockImpact?.({
        scheduleId: props.scheduleId,
        from: props.from,
        to: props.to
      })) ?? null
    if (seq !== requestSeq) return
    impact.value = result
  } catch {
    if (seq !== requestSeq) return
    impact.value = null
  }
}

// Fechar o modal da política dentro dos 300 ms do debounce não pode disparar um
// GET que ninguém mais vai ler.
onBeforeUnmount(() => {
  if (timer) clearTimeout(timer)
  timer = null
  requestSeq += 1
})

watch(
  () => [props.scheduleId, props.from, props.to] as const,
  () => {
    if (timer) clearTimeout(timer)
    // Invalida já: o número velho não pode sobreviver ao recorte que o gerou.
    requestSeq += 1
    impact.value = null
    timer = setTimeout(() => {
      timer = null
      void fetchImpact()
    }, DEBOUNCE_MS)
  },
  { immediate: true }
)
</script>
